'use client';

import { Send } from 'lucide-react';
import * as React from 'react';
import { useEffect, useRef } from 'react';

export interface AiCoachMessage {
	/** Id univoco del messaggio. */
	id: string;
	/** Chi ha scritto il messaggio: l'utente o l'assistente AI. */
	role: 'user' | 'assistant';
	/** Testo del messaggio; vuoto mentre l'assistente sta ancora rispondendo (streaming). */
	text: string;
}

export interface AiCoachChatProps {
	/** Cronologia dei messaggi della conversazione. */
	messages: AiCoachMessage[];
	/** Valore corrente del campo di input. */
	input: string;
	/** Chiamata a ogni digitazione nel campo di input. */
	onInputChange: (v: string) => void;
	/** Chiamata all'invio del messaggio (submit del form o Invio). */
	onSend: (text: string) => void;
	/** Se true, l'assistente sta generando una risposta: disabilita l'input e il bottone invio. */
	streaming: boolean;
	/** Titolo della card. Default: "AI Coach". */
	title?: React.ReactNode;
	/**
	 * Badge accanto al titolo. Nessuno di default — il mock non ne ha uno su
	 * questa card (a differenza di "Raccomandazioni AI", che mostra un
	 * contatore reale).
	 */
	badge?: React.ReactNode;
	/** Messaggio mostrato quando la conversazione è vuota. */
	emptyHint?: React.ReactNode;
	/** Testo segnaposto del campo di input. */
	placeholder?: string;
}

/**
 * Chat con l'AI Coach energetico: bubble dei messaggi con auto-scroll e riga di input.
 * Lo stato della conversazione (messaggi, streaming, invio) è gestito dal chiamante;
 * questo componente è puramente di presentazione.
 */
const AiCoachChat = ({
	messages,
	input,
	onInputChange,
	onSend,
	streaming,
	title = 'AI Coach',
	badge,
	emptyHint = 'Chiedi qualcosa al tuo AI Coach: come consumare meno dalla rete, quando usare gli elettrodomestici, come funziona la CER…',
	placeholder = 'Scrivi un messaggio…',
}: AiCoachChatProps) => {
	const scrollRef = useRef<HTMLDivElement>(null);

	// Keep the newest message in view while streaming.
	useEffect(() => {
		scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
	}, [messages]);

	return (
		<div className="card flex flex-col">
			<div className="card-head">
				<span className="card-title">{title}</span>
				{badge && <span className="card-badge badge-green">{badge}</span>}
			</div>
			{/* Nessuna altezza minima fissa sulla card (tolto `minHeight: 280`) né
			    `flex-1` qui: senza conversazione la card si ferma al testo di
			    invito + riga di input, come nel mock, invece di allungarsi a
			    riempire un'altezza forzata che non ha più motivo di esistere. */}
			<div ref={scrollRef} className="space-y-2 overflow-y-auto" style={{ maxHeight: 260 }}>
				{messages.length === 0 ? (
					<p className="py-4 text-center text-[13px] text-text-muted">{emptyHint}</p>
				) : (
					messages.map((m) => (
						<div
							key={m.id}
							className={
								m.role === 'user'
									? 'ml-auto max-w-[85%] rounded-lg rounded-br-sm bg-accent/10 px-2.5 py-1.5 text-[13px] text-text-primary'
									: 'mr-auto max-w-[90%] whitespace-pre-wrap rounded-lg rounded-bl-sm border border-border bg-panel px-2.5 py-1.5 text-[13px] text-text-primary'
							}
						>
							{m.text || '…'}
						</div>
					))
				)}
			</div>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					onSend(input);
				}}
				className="mt-2 flex items-center gap-1.5"
			>
				<input
					value={input}
					onChange={(e) => onInputChange(e.target.value)}
					placeholder={placeholder}
					disabled={streaming}
					className="flex-1 rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-text-primary placeholder:text-text-muted/50 focus:border-accent focus:outline-none disabled:opacity-50"
				/>
				<button
					type="submit"
					disabled={streaming || !input.trim()}
					aria-label="Invia"
					className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-black transition-opacity hover:opacity-90 disabled:opacity-40"
				>
					<Send className="h-3.5 w-3.5" />
				</button>
			</form>
		</div>
	);
};
AiCoachChat.displayName = 'AiCoachChat';

export { AiCoachChat };
